import { processBatches } from "./batchUtils";
import { createWatermark, applyWatermarkToCanvas } from "./watermark";
import { InferenceServerPrediction } from "@rapid/utils/api/inference";
import { createSafeVideoElement } from "./safeBlobUtils";
import { resolveApiPath } from "@rapid/utils/api/utils";
import type { FFmpeg as FFmpegType, FileData } from "@ffmpeg/ffmpeg";
import type { toBlobURL as toBlobURLType, fetchFile as fetchFileType } from "@ffmpeg/util";
import { isSafariBrowser } from "./browser";
import { getContrastTextColor, getLabelColor } from "@rapid/components/annotation/colors";
import type { PromptLabels } from "@rapid/store/slices/modelIntentSlice";
import { decodeMaskSync } from "./maskDecoderSync";

// FFmpeg info type
export interface FFmpegInfo {
    codec_name?: string;
    codec_long_name?: string;
    width?: number;
    height?: number;
    r_frame_rate?: string;
    avg_frame_rate?: string;
    duration?: string | number;
    nb_frames?: string;
    bit_rate?: string;
    [key: string]: any;
}

// Dynamic imports for FFmpeg - loaded externally, not bundled
let FFmpeg: typeof FFmpegType | null = null;
let toBlobURL: typeof toBlobURLType | null = null;
let fetchFile: typeof fetchFileType | null = null;

// Indirect dynamic import that bypasses webpack's static analysis.
// Webpack rewrites import() calls into __webpack_require__, which breaks
// URL-based imports (e.g. https://host/static/ffmpeg/ffmpeg.js).
// Vite handles these as network fetches natively, but webpack cannot.
// Using new Function() creates a runtime import that webpack won't transform.
const dynamicImport = new Function("url", "return import(url)") as (url: string) => Promise<any>;

// Load FFmpeg modules dynamically
async function loadFFmpegModules() {
    if (FFmpeg && toBlobURL && fetchFile) return { FFmpeg, toBlobURL, fetchFile };

    console.log("🎬 [FFmpeg] Loading FFmpeg modules dynamically...");

    try {
        const origin = typeof window !== "undefined" ? window.location.origin : "";
        const [ffmpegModule, utilModule] = await Promise.all([
            dynamicImport(`${origin}${resolveApiPath("/static/ffmpeg/ffmpeg.js")}`),
            dynamicImport(`${origin}${resolveApiPath("/static/ffmpeg/util.js")}`)
        ]);

        FFmpeg = ffmpegModule.FFmpeg;
        toBlobURL = utilModule.toBlobURL;
        fetchFile = utilModule.fetchFile;

        console.log("🎬 [FFmpeg] ✅ Dynamic modules loaded successfully!");
        return { FFmpeg, toBlobURL, fetchFile };
    } catch (error) {
        console.error("🎬 [FFmpeg] ❌ Failed to load dynamic modules:", error);
        throw new Error(`Failed to load FFmpeg modules: ${error}`);
    }
}

// Global ffmpeg instance to avoid re-initialization
let ffmpegInstance: any = null;
let ffmpegInitPromise: Promise<any> | null = null;

// Pre-load FFmpeg in the background to eliminate initialization delays
export function preloadFFmpeg(): Promise<any> {
    if (ffmpegInitPromise) return ffmpegInitPromise;

    // console.log("🎬 [FFmpeg] Starting background preload for instant trimming...");
    ffmpegInitPromise = initializeFFmpeg();

    // Handle success/failure without blocking
    ffmpegInitPromise.catch((error) => {
        console.warn("🎬 [FFmpeg] Background preload failed, will initialize on demand:", error);
        // Reset the promise so initialization can be retried
        ffmpegInitPromise = null;
    });

    return ffmpegInitPromise;
}

// Helper function to initialize FFmpeg
async function initializeFFmpeg(onProgress?: (progress: number) => void): Promise<any> {
    if (ffmpegInstance) return ffmpegInstance;

    // console.log("🎬 [FFmpeg] Initializing FFmpeg instance...");

    // Load FFmpeg modules dynamically first
    const {
        FFmpeg: FFmpegClass,
        toBlobURL: toBlobURLFunc,
        fetchFile: fetchFileFunc
    } = await loadFFmpegModules();

    // Update global references
    FFmpeg = FFmpegClass;
    toBlobURL = toBlobURLFunc;
    fetchFile = fetchFileFunc;

    if (!FFmpeg) throw new Error("🎬 [FFmpeg] Failed to load");

    ffmpegInstance = new FFmpeg();

    // Report initialization progress
    if (onProgress) onProgress(10);

    try {
        // console.log("🎬 [FFmpeg] Loading FFmpeg with default configuration...");

        // In production and localhost, try to use same-origin files to avoid CORS issues
        if (
            typeof window !== "undefined" &&
            (window.location.hostname.includes("roboflow.one") ||
                window.location.hostname.includes("localhost"))
        ) {
            try {
                // Use backend-served FFmpeg files - guaranteed same-origin, no CORS issues
                const [coreBlob, wasmBlob, workerBlob] = await Promise.all([
                    toBlobURL(
                        `${window.location.origin}${resolveApiPath("/static/ffmpeg/ffmpeg-core.js")}`,
                        "text/javascript"
                    ),
                    toBlobURL(
                        `${window.location.origin}${resolveApiPath("/static/ffmpeg/ffmpeg-core.wasm")}`,
                        "application/wasm"
                    ),
                    toBlobURL(
                        `${window.location.origin}${resolveApiPath("/static/ffmpeg/ffmpeg-worker.js")}`,
                        "text/javascript"
                    )
                ]);

                // Add timeout wrapper to debug hanging load
                const loadWithTimeout = new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => {
                        reject(new Error("FFmpeg load timeout after 30 seconds"));
                    }, 30000);

                    ffmpegInstance
                        .load({ coreURL: coreBlob, wasmURL: wasmBlob, workerURL: workerBlob })
                        .then((result: any) => {
                            clearTimeout(timeout);
                            resolve(result);
                        })
                        .catch((error: any) => {
                            clearTimeout(timeout);
                            console.error("🎬 [FFmpeg] Load promise rejected:", error);
                            reject(error);
                        });
                });

                await loadWithTimeout;

                if (onProgress) onProgress(20);
                return ffmpegInstance;
            } catch (backendError) {
                console.warn("🎬 [FFmpeg] Backend-served files failed:", backendError);
                // Continue to fallback methods below
            }
        }

        // Try simple default loading first
        try {
            await ffmpegInstance.load();
            if (onProgress) onProgress(20);
            return ffmpegInstance;
        } catch (defaultError) {
            console.warn("🎬 [FFmpeg] Default loading failed:", defaultError);
        }

        // If default loading fails, try CDN fallback with proper 0.12 URLs
        // Reset instance for retry
        ffmpegInstance = null;
        ffmpegInstance = new FFmpeg();
        try {
            // Use correct 0.12 CDN URLs for single-threaded fallback
            await ffmpegInstance.load({
                coreURL: await toBlobURL(
                    "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd/ffmpeg-core.js",
                    "text/javascript"
                ),
                wasmURL: await toBlobURL(
                    "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd/ffmpeg-core.wasm",
                    "application/wasm"
                )
                // No workerURL - ensures single-threaded mode to avoid SharedArrayBuffer issues
            });

            if (onProgress) onProgress(20);
            return ffmpegInstance;
        } catch (cdnError) {
            console.error("🎬 [FFmpeg] CDN fallback failed:", cdnError);

            // Try one more time with even simpler single-threaded loading
            // console.log("🎬 [FFmpeg] Trying minimal single-threaded loading...");

            ffmpegInstance = null;
            ffmpegInstance = new FFmpeg();

            try {
                // Minimal loading without any custom URLs
                await ffmpegInstance.load();
                if (onProgress) onProgress(20);
                return ffmpegInstance;
            } catch (minimalError) {
                console.error("🎬 [FFmpeg] Minimal loading also failed:", minimalError);
                throw cdnError; // Throw the original CDN error for better debugging
            }
        }
    } catch (error) {
        console.error("🎬 [FFmpeg] All initialization methods failed:", error);

        // Reset instance so it can be retried
        ffmpegInstance = null;

        throw new Error(`FFmpeg initialization failed: ${error}`);
    }
}

const expectedMinimumReduction = 0.05; // At least 5% reduction expected

// FFmpeg command helpers for trimVideo
async function trimVideoWithStreamCopy(
    ffmpeg: any,
    inputFileName: string,
    outputFileName: string,
    startTime: number,
    duration: number
) {
    const command = [
        "-i",
        inputFileName,
        "-ss",
        startTime.toString(),
        "-t",
        duration.toString(),
        "-c:v",
        "copy",
        "-an",
        "-avoid_negative_ts",
        "make_zero",
        outputFileName
    ];
    await ffmpeg.exec(command);
}

async function trimVideoWithStreamCopyFallback(
    ffmpeg: any,
    inputFileName: string,
    outputFileName: string,
    startTime: number,
    duration: number
) {
    const command = [
        "-ss",
        startTime.toString(),
        "-i",
        inputFileName,
        "-t",
        duration.toString(),
        "-c:v",
        "copy",
        "-an",
        "-avoid_negative_ts",
        "make_zero",
        outputFileName
    ];
    await ffmpeg.exec(command);
}

async function trimVideoWithFastReencoding(
    ffmpeg: any,
    inputFileName: string,
    outputFileName: string,
    startTime: number,
    duration: number
) {
    const command = [
        "-ss",
        startTime.toString(),
        "-i",
        inputFileName,
        "-t",
        duration.toString(),
        "-c:v",
        "libx264",
        "-preset",
        "ultrafast",
        "-tune",
        "zerolatency",
        "-crf",
        "35",
        "-profile:v",
        "baseline",
        "-threads",
        "0",
        "-pix_fmt",
        "yuv420p",
        "-an",
        "-f",
        "mp4",
        outputFileName
    ];
    await ffmpeg.exec(command);
}

// Helper function to trim video using FFmpeg.wasm
export const trimVideo = async ({
    file,
    startTime,
    endTime,
    fpsRate = 30, // Default FPS - should be passed from hook using useFps()
    onProgress,
    fastMode = true // New parameter: true for keyframe-aligned (fast), false for precise (slow)
}: {
    file: File;
    startTime: number;
    endTime: number;
    fpsRate: number;
    onProgress?: (progress: number) => void;
    fastMode: boolean;
}): Promise<File> => {
    // console.log("🐞[DEBUG - trimVideo]", { file, startTime, endTime, fpsRate, fastMode });
    // Create input filename with proper extension
    const inputFileName = `input.${file.name.split(".").pop() || "mp4"}`;
    const outputFileName = "output.mp4";

    // load ffmpeg
    let ffmpeg: FFmpegType;
    try {
        // Use pre-loaded FFmpeg if available, otherwise initialize
        ffmpeg = ffmpegInitPromise ? await ffmpegInitPromise : await initializeFFmpeg(onProgress);

        if (!ffmpeg) throw new Error("FFmpeg utility not loaded");
        if (!fetchFile) throw new Error("FFmpeg fetchFile utility not loaded");
        if (!toBlobURL) throw new Error("FFmpeg toBlobURL utility not loaded");

        if (onProgress) onProgress(ffmpegInitPromise ? 40 : 30);
    } catch (error) {
        console.error("Error initializing FFmpeg:", error);
        throw new Error(`Failed to initialize FFmpeg: ${error}`);
    }

    try {
        // Convert file to Uint8Array for FFmpeg
        const inputData = await fetchFile(file);

        // Create a copy to prevent FFmpeg from consuming the original buffer
        const inputDataCopy = new Uint8Array(inputData);

        // Write input file copy to FFmpeg virtual file system
        await ffmpeg.writeFile(inputFileName, inputDataCopy);

        if (onProgress) onProgress(40);

        // Calculate duration for progress tracking
        const duration = endTime - startTime;

        // Set up progress tracking
        let lastProgressUpdate = 0;
        ffmpeg.on("progress", ({ progress }: { progress: number }) => {
            const now = Date.now();
            if (onProgress && now - lastProgressUpdate > 100) {
                // Map FFmpeg progress (0-1) to our remaining progress range (40-95)
                const mappedProgress = 40 + progress * 55;
                onProgress(Math.min(mappedProgress, 95));
                lastProgressUpdate = now;
            }
        });

        // Determine if we should use stream copy (fast but imprecise) or re-encoding (slower but precise)
        // Stream copy can only cut at keyframes, so for precise trimming we need re-encoding
        const useKeyframeAlignment = fastMode; // Use fastMode parameter to control processing
        const canUseStreamCopy = useKeyframeAlignment; // Enable stream copy when using keyframe alignment

        // For keyframe alignment, adjust times to reasonable keyframe boundaries
        let adjustedStartTime = startTime;
        let adjustedEndTime = endTime;
        let adjustedDuration = duration;

        if (useKeyframeAlignment) {
            // Round to nearest 0.5s for reasonable keyframe alignment
            // IMPORTANT: Never exceed the requested duration to avoid UI limits
            adjustedStartTime = Math.floor(startTime * 2) / 2;

            // Calculate max allowed end time to stay within duration limit
            const maxAllowedEndTime = adjustedStartTime + duration;
            adjustedEndTime = Math.floor(endTime * 2) / 2;

            // Ensure we don't exceed the original requested duration
            if (adjustedEndTime > maxAllowedEndTime) {
                adjustedEndTime = Math.floor(maxAllowedEndTime * 2) / 2;
            }

            adjustedDuration = adjustedEndTime - adjustedStartTime;

            // Safety check: ensure duration doesn't exceed original request
            if (adjustedDuration > duration) {
                adjustedDuration = duration;
                adjustedEndTime = adjustedStartTime + adjustedDuration;
            }
        }

        if (canUseStreamCopy) {
            // Fast keyframe-aligned stream copy for speed
            // console.log("🎬 [FFmpeg] Using keyframe-aligned stream copy for fast processing!");

            await trimVideoWithStreamCopy(
                ffmpeg,
                inputFileName,
                outputFileName,
                adjustedStartTime,
                adjustedDuration
            );

            // Verify that stream copy actually worked by checking output size
            const outputData = await ffmpeg.readFile(outputFileName);
            const actualReduction = (inputData.length - outputData.length) / inputData.length;

            if (actualReduction < expectedMinimumReduction) {
                // Clean up failed output
                await ffmpeg.deleteFile(outputFileName);

                // Try fallback stream copy command with different parameter order
                await trimVideoWithStreamCopyFallback(
                    ffmpeg,
                    inputFileName,
                    outputFileName,
                    adjustedStartTime,
                    adjustedDuration
                );

                const fallbackOutput = await ffmpeg.readFile(outputFileName);
                const fallbackReduction =
                    (inputData.length - fallbackOutput.length) / inputData.length;

                if (fallbackReduction < expectedMinimumReduction) {
                    // Clean up failed output
                    await ffmpeg.deleteFile(outputFileName);

                    // Fall back to super-fast re-encoding for guaranteed results
                    await trimVideoWithFastReencoding(
                        ffmpeg,
                        inputFileName,
                        outputFileName,
                        adjustedStartTime,
                        adjustedDuration
                    );
                }
            }

            // check if what we generated is a valid video file
            const videoFileValid = await isValidVideoFile(outputData);
            if (!videoFileValid) {
                // Clean up failed output
                await ffmpeg.deleteFile(outputFileName);

                await trimVideoWithFastReencoding(
                    ffmpeg,
                    inputFileName,
                    outputFileName,
                    adjustedStartTime,
                    adjustedDuration
                );
            }
        } else {
            // Use super-fast re-encoding for frame-accurate trimming
            await trimVideoWithFastReencoding(
                ffmpeg,
                inputFileName,
                outputFileName,
                adjustedStartTime,
                adjustedDuration
            );
        }

        if (onProgress) onProgress(95);

        // Read the output file
        const outputData = await ffmpeg.readFile(outputFileName);

        // Check final results
        const compressionRatio = (1 - outputData.length / inputData.length) * 100;
        if (Math.round(compressionRatio) === 100) {
            console.warn("🎬 [FFmpeg] Video compression achieved 100% reduction!");
            throw new Error("Trimming failed: output file is empty");
        }

        if (onProgress) onProgress(100);

        // Create blob and file
        const outputBlob = new Blob([outputData], { type: "video/mp4" });
        const trimmedFileName = file.name.replace(/\.[^/.]+$/, "_trimmed.mp4");
        const trimmedFile = new File([outputBlob], trimmedFileName, { type: "video/mp4" });

        return trimmedFile;
    } catch (error) {
        console.error("Error trimming video with FFmpeg:", error);
        throw new Error(`Failed to trim video: ${error}`);
    } finally {
        // Clean up files from FFmpeg virtual file system
        await ffmpeg.deleteFile(inputFileName);
        await ffmpeg.deleteFile(outputFileName);
    }
};

export const isValidVideoFile = async (file: Blob | FileData): Promise<boolean> => {
    // Additional validation: check if the file is a valid video

    const blob = file instanceof Blob ? file : new Blob([file], { type: "video/mp4" });

    // Convert Blob/FileData to File for the video manager
    const videoFile =
        blob instanceof File ? blob : new File([blob], "validation.mp4", { type: "video/mp4" });

    try {
        const videoManager = createSafeVideoElement(videoFile, {
            muted: true,
            preload: "auto",
            crossOrigin: "anonymous"
        });
        const video = videoManager.element;

        await new Promise((resolve, reject) => {
            video.onloadedmetadata = () => {
                videoManager.cleanup();
                resolve(true);
            };
            video.onerror = () => {
                videoManager.cleanup();
                reject(new Error("Generated video file is invalid"));
            };
            video.load();
        });
        return true;
    } catch (validationError) {
        console.error(`Video validation failed: ${validationError}`);
    }
    return false;
};

// Helper function to format time for display
export const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

// Configuration for video processing
const VIDEO_PROCESSING_CONFIG = {
    FRAME_EXTRACTION_BATCH_SIZE: 4,
    FRAME_PROCESSING_BATCH_SIZE: 8,
    EXTRACTION_PROGRESS_WEIGHT: 0.5, // 50% of total progress
    PROCESSING_PROGRESS_WEIGHT: 0.3, // 30% of total progress
    JPEG_QUALITY: 0.85,
    FFMPEG_CRF: "23"
} as const;

// Drawing constants matching BoundingBoxStrategy.ts for consistency
const DRAWING_CONSTANTS = {
    BOX_CORNER_RADIUS: 4,
    LABEL_PADDING_X: 4,
    LABEL_PADDING_Y_TOP: 2,
    LABEL_PADDING_Y_BOTTOM: 4,
    LABEL_OFFSET_X_FOR_RADIUS: 2,
    LABEL_TEXT_COLOR: "white",
    BASE_FONT_SIZE: 11,
    DEFAULT_STROKE_WIDTH: 1.5
} as const;

// Types for better type safety
interface VideoDimensions {
    width: number;
    height: number;
}

export interface VideoMetadata {
    duration: number; // in seconds
    dimensions: VideoDimensions;
    fps: number;
    frameCount: number;
    isTrimming: boolean;
}

interface AnnotationOptions {
    confidenceThreshold: number;
    fontScale: number;
    boxThickness: number;
}

type MaskData = { counts: string; size: [number, number] };

// Resource-safe video element wrapper
class VideoResource {
    private videoManager: ReturnType<typeof createSafeVideoElement> | null = null;
    private video: HTMLVideoElement | null = null;

    // Video manager will be created when loadFile is called

    async loadFile(file: File, fps: number = 30): Promise<VideoMetadata> {
        // Clean up any existing video manager
        if (this.videoManager && !this.videoManager.isCleanedUp) {
            this.videoManager.cleanup();
        }

        // Create new video manager
        this.videoManager = createSafeVideoElement(file, {
            muted: true,
            preload: "metadata",
            crossOrigin: "anonymous"
        });
        this.video = this.videoManager.element;

        return new Promise((resolve, reject) => {
            if (!this.video) {
                reject(new Error("Video element not available"));
                return;
            }

            this.video.onloadedmetadata = () => {
                if (!this.video) {
                    reject(new Error("Video element not available"));
                    return;
                }

                resolve({
                    duration: this.video.duration,
                    dimensions: { width: this.video.videoWidth, height: this.video.videoHeight },
                    fps: fps,
                    frameCount: Math.round(this.video.duration * fps),
                    isTrimming: false
                });
            };
            this.video.onerror = (err) => {
                reject(new Error("Failed to load video metadata"));
            };
            this.video.load();
        });
    }

    async seekToTime(targetTime: number): Promise<void> {
        if (!this.video) {
            throw new Error("Video not loaded");
        }

        this.video.currentTime = targetTime;
        return new Promise((resolve, reject) => {
            if (!this.video) {
                reject(new Error("Video element not available"));
                return;
            }
            this.video.onseeked = () => resolve();
            this.video.onerror = () => reject(new Error("Failed to seek video"));
        });
    }

    drawToCanvas(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): ImageData {
        if (!this.video) {
            throw new Error("Video not loaded");
        }
        ctx.drawImage(this.video, 0, 0, canvas.width, canvas.height);
        return ctx.getImageData(0, 0, canvas.width, canvas.height);
    }

    dispose(): void {
        if (this.videoManager && !this.videoManager.isCleanedUp) {
            this.videoManager.cleanup();
        }
        this.videoManager = null;
        this.video = null;
    }
}

// Canvas wrapper for consistent setup
function createCanvas(dimensions: VideoDimensions): {
    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
} {
    const canvas = document.createElement("canvas");
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
        throw new Error("Could not create canvas context");
    }

    return { canvas, ctx };
}

// Extract single frame with proper resource management
export async function extractSingleFrameViaCanvas(
    videoFile: File,
    targetTime: number,
    videoDimensions: VideoDimensions,
    fps: number = 30
): Promise<ImageData> {
    const videoResource = new VideoResource();
    const { canvas, ctx } = createCanvas(videoDimensions);

    try {
        await videoResource.loadFile(videoFile, fps);
        await videoResource.seekToTime(targetTime);
        const imageData = videoResource.drawToCanvas(canvas, ctx);
        return imageData;
    } finally {
        // Forces browser to deallocate pixel buffer
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = 0;
        canvas.height = 0;
        // Removes DOM reference
        canvas.remove();
    }
}

export async function imageDataToFile(imageData: ImageData, name: string = "image") {
    const blob = await imageDataToBlob(imageData);
    return new File([blob], name + ".jpg", { type: "image/jpg" });
}

// Get video metadata efficiently
export async function getVideoMetadata(videoFile: File, fps: number = 30): Promise<VideoMetadata> {
    const videoResource = new VideoResource();
    try {
        return await videoResource.loadFile(videoFile, fps);
    } finally {
        videoResource.dispose();
    }
}

// Main frame extraction function with parallel processing
export async function extractFramesFromVideoViaCanvas(
    videoFile: File,
    frameCount: number,
    onProgress?: (progress: number) => void
): Promise<ImageData[]> {
    const metadata = await getVideoMetadata(videoFile);

    // Use consistent frame indexing logic to match backend
    // Calculate target times using the same method as backend frame timestamp calculation
    const frameTasks = Array.from({ length: frameCount }, (_, i) => ({
        targetTime: i / metadata.fps, // frameIndex / fps - matches backend calcFrameTimestampFromIndex
        index: i
    }));

    console.log(`🎬 [extractFramesFromVideoViaCanvas] 🎬 frameTasks`, frameTasks);

    // Process frames in parallel batches
    return processBatches(
        frameTasks,
        VIDEO_PROCESSING_CONFIG.FRAME_EXTRACTION_BATCH_SIZE,
        async (task) =>
            extractSingleFrameViaCanvas(videoFile, task.targetTime, metadata.dimensions),
        (completed, total) => {
            const progress =
                (completed / total) * VIDEO_PROCESSING_CONFIG.EXTRACTION_PROGRESS_WEIGHT * 100;
            onProgress?.(
                Math.min(progress, VIDEO_PROCESSING_CONFIG.EXTRACTION_PROGRESS_WEIGHT * 100)
            );
        }
    );
}

// Helper function to draw rounded rectangle path
const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.arcTo(x + width, y, x + width, y + radius, radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius);
    ctx.lineTo(x + radius, y + height);
    ctx.arcTo(x, y + height, x, y + height - radius, radius);
    ctx.lineTo(x, y + radius);
    ctx.arcTo(x, y, x + radius, y, radius);
    ctx.closePath();
};

const getPredictionMask = (prediction: InferenceServerPrediction): MaskData | null => {
    const masks = prediction.masks;
    if (!masks) return null;

    const mask = Array.isArray(masks) ? masks[0] : masks;
    if (!mask?.counts || !Array.isArray(mask.size) || mask.size.length < 2) return null;

    return mask as MaskData;
};

const drawMaskOnFrame = (
    ctx: CanvasRenderingContext2D,
    masks: MaskData,
    color: string
): boolean => {
    try {
        const decoded = decodeMaskSync(masks, false);
        const maskWidth = masks.size[1];
        const maskHeight = masks.size[0];

        const maskCanvas = document.createElement("canvas");
        maskCanvas.width = decoded.imageData.width;
        maskCanvas.height = decoded.imageData.height;

        const maskCtx = maskCanvas.getContext("2d");
        if (!maskCtx) return false;

        maskCtx.putImageData(decoded.imageData, 0, 0);
        maskCtx.globalCompositeOperation = "source-atop";
        maskCtx.fillStyle = color;
        maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);

        const scaleX = ctx.canvas.width / maskWidth;
        const scaleY = ctx.canvas.height / maskHeight;

        ctx.drawImage(
            maskCanvas,
            decoded.offsetX * scaleX,
            decoded.offsetY * scaleY,
            decoded.imageData.width * scaleX,
            decoded.imageData.height * scaleY
        );

        return true;
    } catch (error) {
        console.warn("🎬 [DOWNLOAD] Failed to draw prediction mask:", error);
        return false;
    }
};

// Helper function to draw predictions on a frame matching live annotation styling
async function drawPredictionsOnFrame(
    frameData: ImageData,
    predictions: InferenceServerPrediction[],
    options: AnnotationOptions & {
        includeWatermark?: boolean;
        watermarkQrUrl?: string;
        watermarkCanvas?: HTMLCanvasElement | null;
        promptLabels?: PromptLabels;
    }
): Promise<ImageData> {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
        return frameData; // Return original if we can't draw
    }

    canvas.width = frameData.width;
    canvas.height = frameData.height;

    // Put the original frame data on canvas
    ctx.putImageData(frameData, 0, 0);

    // Filter predictions by confidence
    const visiblePredictions = predictions.filter(
        (p) => p.confidence >= options.confidenceThreshold
    );

    // Scale font size based on frame height (similar to live annotations)
    const scaledFontSize = Math.max(
        DRAWING_CONSTANTS.BASE_FONT_SIZE,
        frameData.height * options.fontScale * 0.02
    );
    const LABEL_FONT = `${scaledFontSize}px monospace`; // Match font family

    // Improve drawing quality
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.font = LABEL_FONT;
    ctx.lineWidth = options.boxThickness;

    // Draw each prediction with exact styling match
    visiblePredictions.forEach((prediction) => {
        const baseColor = getLabelColor(options.promptLabels, prediction.class);
        const mask = getPredictionMask(prediction);
        if (mask) {
            drawMaskOnFrame(ctx, mask, baseColor);
        }

        // Calculate box coordinates (center-based like live annotations)
        const tlx = prediction.x - prediction.width / 2;
        const tly = prediction.y - prediction.height / 2;
        const brx = prediction.x + prediction.width / 2;
        const bry = prediction.y + prediction.height / 2;

        ctx.strokeStyle = baseColor;
        ctx.lineWidth = options.boxThickness;

        // Draw rounded rectangle box (matching BoundingBoxStrategy)
        drawRoundedRect(
            ctx,
            tlx,
            tly,
            prediction.width,
            prediction.height,
            DRAWING_CONSTANTS.BOX_CORNER_RADIUS
        );
        ctx.stroke();

        // Prepare label text (matching live annotation format)
        const label = `${prediction.class} (${Math.round(prediction.confidence * 100)}%)`;
        const textMetrics = ctx.measureText(label);
        const textWidth = textMetrics.width;
        const textHeight = scaledFontSize;

        // Calculate label background dimensions and position
        const labelBgHeight =
            textHeight +
            DRAWING_CONSTANTS.LABEL_PADDING_Y_TOP +
            DRAWING_CONSTANTS.LABEL_PADDING_Y_BOTTOM;
        const labelBgWidth = textWidth + DRAWING_CONSTANTS.LABEL_PADDING_X * 2;
        const labelBgTlx = tlx + DRAWING_CONSTANTS.LABEL_OFFSET_X_FOR_RADIUS;
        const labelBgTly = Math.max(0, tly - labelBgHeight); // Clamp to canvas bounds

        // Draw label background with rounded corners (matching BoundingBoxStrategy)
        ctx.fillStyle = baseColor; // Background matches box color
        drawRoundedRect(
            ctx,
            labelBgTlx,
            labelBgTly,
            labelBgWidth,
            labelBgHeight,
            DRAWING_CONSTANTS.BOX_CORNER_RADIUS
        );
        ctx.fill();

        // Draw label text with contrast-based color
        ctx.fillStyle = getContrastTextColor(baseColor);
        ctx.fillText(
            label,
            labelBgTlx + DRAWING_CONSTANTS.LABEL_PADDING_X,
            labelBgTly + textHeight + DRAWING_CONSTANTS.LABEL_PADDING_Y_TOP
        );
    });

    // Apply watermark if requested
    if (options.includeWatermark) {
        // Use pre-created watermark if available, otherwise create it
        let watermarkCanvas = options.watermarkCanvas;
        if (!watermarkCanvas) {
            watermarkCanvas = await createWatermark(frameData.width, frameData.height, {
                qrUrl: options.watermarkQrUrl
            });
        }

        if (watermarkCanvas) {
            applyWatermarkToCanvas(ctx, watermarkCanvas, canvas.width, canvas.height);
        }
    }

    // Return the annotated frame data
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

// Helper function to convert ImageData to blob
function imageDataToBlob(imageData: ImageData): Promise<Blob> {
    return new Promise((resolve) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d")!;
        canvas.width = imageData.width;
        canvas.height = imageData.height;
        ctx.putImageData(imageData, 0, 0);
        canvas.toBlob((blob) => resolve(blob!), "image/jpeg", VIDEO_PROCESSING_CONFIG.JPEG_QUALITY);
    });
}

// Main function to create annotated video
export const createAnnotatedVideo = async (
    videoFile: File,
    predictionsForFile: Record<number, Array<InferenceServerPrediction>>,
    confidenceThreshold: number,
    outputFps: number = 30,
    fontScale: number = 0.4,
    boxThickness: number = 2,
    onProgress?: (progress: number) => void,
    includeWatermark: boolean = false,
    watermarkQrUrl: string = "https://rapid.roboflow.com",
    promptLabels?: PromptLabels
): Promise<File> => {
    try {
        onProgress?.(5);

        // Use pre-loaded FFmpeg if available, otherwise initialize
        const ffmpeg = ffmpegInitPromise ? await ffmpegInitPromise : await initializeFFmpeg();

        onProgress?.(10);

        // Determine how many frames we need based on predictions
        const frameNumbers = Object.keys(predictionsForFile)
            .map((key) => parseInt(key, 10))
            .filter((num) => !isNaN(num))
            .sort((a, b) => a - b);

        if (frameNumbers.length === 0) {
            throw new Error("No frames with predictions found");
        }

        const maxFrame = Math.max(...frameNumbers);
        const frameCount = maxFrame + 1;

        onProgress?.(15);

        // Extract frames from video
        const frames = await extractFramesFromVideoViaCanvas(
            videoFile,
            frameCount,
            (extractProgress) => {
                onProgress?.(15 + extractProgress * 0.3); // 15-30%
            }
        );

        onProgress?.(30);

        // Process frames and add annotations in parallel batches for speed

        // Create watermark once for all video frames (assuming consistent dimensions)
        let reusableWatermarkCanvas: HTMLCanvasElement | null = null;
        if (includeWatermark && frames.length > 0) {
            // Use first frame dimensions to create the watermark once
            const firstFrame = frames[0];
            reusableWatermarkCanvas = await createWatermark(firstFrame.width, firstFrame.height, {
                qrUrl: watermarkQrUrl
            });
        }

        // Create frame processing tasks
        const frameProcessingTasks = frames.map((frameData, index) => ({
            frameData,
            predictions: predictionsForFile[index] || [],
            index
        }));

        // Process frames in parallel batches using our generic processor
        const annotatedFrames = await processBatches(
            frameProcessingTasks,
            VIDEO_PROCESSING_CONFIG.FRAME_PROCESSING_BATCH_SIZE,
            async (task) => {
                // Draw predictions on this frame
                const annotatedFrameData = await drawPredictionsOnFrame(
                    task.frameData,
                    task.predictions,
                    {
                        confidenceThreshold,
                        fontScale,
                        boxThickness,
                        includeWatermark,
                        watermarkQrUrl,
                        watermarkCanvas: reusableWatermarkCanvas,
                        promptLabels
                    }
                );

                // Convert to blob
                return await imageDataToBlob(annotatedFrameData);
            },
            (completed, total) => {
                // Update progress for frame processing (30-60%)
                const baseProgress = 30;
                const progressRange = 30;
                const progress = baseProgress + (completed / total) * progressRange;
                onProgress?.(Math.min(progress, 60));
            }
        );

        onProgress?.(60);

        // Write frames to FFmpeg
        for (let i = 0; i < annotatedFrames.length; i++) {
            const frameData = await fetchFile(annotatedFrames[i]);
            await ffmpeg.writeFile(`frame_${i.toString().padStart(6, "0")}.jpg`, frameData);
        }

        onProgress?.(70);

        // Create video from frames
        const outputFileName = "output_annotated.mp4";

        // Set up progress tracking for FFmpeg
        let lastProgressUpdate = 0;
        ffmpeg.on("progress", ({ progress }: { progress: number }) => {
            const now = Date.now();
            if (onProgress && now - lastProgressUpdate > 100) {
                // Map FFmpeg progress (0-1) to our remaining progress range (70-95)
                const mappedProgress = 70 + progress * 25;
                onProgress(Math.min(mappedProgress, 95));
                lastProgressUpdate = now;
            }
        });

        // FFmpeg command to create video from image sequence - optimized for speed
        const ffmpegCommand = [
            "-framerate",
            outputFps.toString(),
            "-i",
            "frame_%06d.jpg",
            "-c:v",
            "libx264",
            "-preset",
            "ultrafast", // Fast encoding (boxes are already high quality)
            "-crf",
            VIDEO_PROCESSING_CONFIG.FFMPEG_CRF, // Reasonable quality, configurable
            "-pix_fmt",
            "yuv420p",
            "-movflags",
            "+faststart", // Enable fast start for web playback
            outputFileName
        ];

        await ffmpeg.exec(ffmpegCommand);

        onProgress?.(95);

        // Read the output file
        const outputData = await ffmpeg.readFile(outputFileName);

        // Clean up all files from FFmpeg virtual file system
        for (let i = 0; i < annotatedFrames.length; i++) {
            await ffmpeg.deleteFile(`frame_${i.toString().padStart(6, "0")}.jpg`);
        }
        await ffmpeg.deleteFile(outputFileName);

        onProgress?.(100);

        // Create the final annotated video file
        const outputBlob = new Blob([outputData], { type: "video/mp4" });
        const annotatedFileName = videoFile.name.replace(/\.[^/.]+$/, "_roboflow_annotated.mp4");
        const annotatedFile = new File([outputBlob], annotatedFileName, { type: "video/mp4" });

        return annotatedFile;
    } catch (error) {
        throw new Error(`Failed to create annotated video: ${error}`);
    }
};

// Helper functions to generate file thumbnails
/**
 * Generate a thumbnail for a video file at a specific time position using FFmpeg
 */

type ThumbnailRequest = {
    currentTime: number;
    index?: number; // For tracking results
};

type ThumbnailResult = { time: number; thumbnail: string | null; index: number };

export enum ThumbnailMode {
    FAST = "fast",
    ACCURATE = "accurate"
}

// ---------- helpers for @ffmpeg/ffmpeg 0.12+ ----------

async function ensureDir(ffmpeg: any, dir: string) {
    try {
        await ffmpeg.createDir(dir);
    } catch {
        /* EEXIST ok */
    }
}

type PreparedVideoContext = {
    ffmpeg: any;
    fetchFile: any;
    inputFileName: string;
    cleanup: () => Promise<void>;
};

/**
 * Prepare a video file for thumbnail extraction by loading it into FFmpeg filesystem once
 */
async function prepareVideoForThumbnails(file: File): Promise<PreparedVideoContext> {
    // Initialize FFmpeg
    const ffmpeg = await initializeFFmpeg();

    if (!ffmpeg || !fetchFile) {
        throw new Error("FFmpeg not available");
    }

    const timestamp = Date.now();
    const extension = file.name.split(".").pop() || "mp4";
    const inputFileName = `thumb_prep_${timestamp}.${extension}`;

    // Write video file to FFmpeg filesystem once
    await ffmpeg.writeFile(inputFileName, await fetchFile(file));

    // Return context with cleanup function
    return {
        ffmpeg,
        fetchFile,
        inputFileName,
        cleanup: async () => {
            try {
                await ffmpeg.deleteFile(inputFileName);
            } catch (error) {
                console.warn(`🎬 [prepareVideoForThumbnails] Failed to cleanup: ${error}`);
            }
        }
    };
}

/**
 * Captures FFmpeg logs during execution of a function, then properly detaches the logger.
 * This utility is essential for debugging video processing operations and handling errors
 * that might only be visible in FFmpeg's output logs.
 *
 * @param fn - The async function to execute while capturing logs
 * @returns Promise containing both the function result and captured log text
 */
async function captureLogs<T>(
    ffmpeg: any,
    fn: () => Promise<T>
): Promise<{ result: T; text: string }> {
    // Array to collect log messages during execution
    const lines: string[] = [];

    // Handler function that extracts message content from log events
    const handler = (e: any) => {
        const msg = e?.message ?? "";
        if (msg) lines.push(String(msg));
    };

    // Attach log event listener
    (ffmpeg as any).on("log", handler);

    try {
        // Execute the provided function while logs are being captured
        const result = await fn();
        return { result, text: lines.join("\n") };
    } finally {
        // Always clean up the logger attachment to prevent memory leaks
        (ffmpeg as any).off("log", handler);
    }
}

/**
 * Lists files in a directory within FFmpeg's virtual file system.
 *
 * @param ffmpeg - The FFmpeg instance to use for file system operations
 * @param dir - The directory path to list files from
 * @returns Promise resolving to array of file names, empty array if listing fails
 */
async function listFiles(ffmpeg: any, dir: string): Promise<string[]> {
    try {
        const listing = await ffmpeg.listDir(dir);

        // FFmpeg.js 0.12+ returns an array of file entries
        if (Array.isArray(listing)) {
            return listing.map((e: any) => (typeof e === "string" ? e : e?.name)).filter(Boolean);
        }

        return [];
    } catch {
        // Return empty array if directory listing fails
        return [];
    }
}

/**
 * Extracts the time base (tbn) value from FFmpeg log output.
 * The time base defines the unit for PTS (Presentation Time Stamp) values -
 * PTS timestamps are in units of 1/tbn seconds.
 *
 * Example log format: "Stream #0:0: Video: h264, yuv420p, 1920x1080, 30 tbn"
 *
 * @param logText - FFmpeg log output containing stream information
 * @returns The time base value as a number, or null if not found or invalid
 */
function parseTbnFrom(logText: string): number | null {
    // Look for pattern like ", 30 tbn" in the log text
    const timeBaseMatch = logText.match(/,\s*(\d+)\s*tbn/);

    if (timeBaseMatch) {
        const timeBaseValue = Number(timeBaseMatch[1]);

        // Ensure the extracted value is a valid positive number
        if (Number.isFinite(timeBaseValue) && timeBaseValue > 0) {
            return timeBaseValue;
        }
    }

    // Return null if no valid time base found
    return null;
}

/**
 * Extracts the frame rate (fps) value from FFmpeg log output.
 * The frame rate indicates how many frames per second the video plays at,
 * which is essential for accurate timestamp calculations and video processing.
 *
 * Example log format: "Stream #0:0: Video: h264, yuv420p, 1920x1080, 25 fps, 25 tbr"
 *
 * @param logText - FFmpeg log output containing stream information
 * @returns The frame rate value as a number, or null if not found or invalid
 */
function parseFpsFrom(logText: string): number | null {
    // Look for pattern like ", 25 fps" or ", 29.97 fps" in the log text
    const frameRateMatch = logText.match(/,\s*(\d+(?:\.\d+)?)\s*fps[,\s]/);

    if (frameRateMatch) {
        const frameRateValue = Number(frameRateMatch[1]);

        // Ensure the extracted value is a valid positive number
        if (Number.isFinite(frameRateValue) && frameRateValue > 0) {
            return frameRateValue;
        }
    }

    // Return null if no valid frame rate found
    return null;
}

/**
 * Finds the URL of the keyframe closest to a given timestamp using binary search.
 * This is used for efficient thumbnail/preview generation by finding the nearest
 * available keyframe to avoid expensive video seeking operations.
 *
 * @param keyframes - Array of keyframe objects with timestamp (sec) and URL, sorted by timestamp
 * @param targetTime - The target timestamp in seconds to find the nearest keyframe for
 * @returns The URL of the closest keyframe, or null if no keyframes available
 */
function nearestUrl(keyframes: { sec: number; url: string }[], targetTime: number): string | null {
    if (!keyframes.length) return null;

    // Binary search to find the insertion point for the target time
    let leftIndex = 0;
    let rightIndex = keyframes.length - 1;

    while (leftIndex <= rightIndex) {
        const midIndex = (leftIndex + rightIndex) >> 1; // Bit shift for fast division by 2

        if (keyframes[midIndex].sec < targetTime) {
            leftIndex = midIndex + 1;
        } else {
            rightIndex = midIndex - 1;
        }
    }

    // Get the keyframes on either side of the target time
    const leftCandidate = keyframes[Math.max(0, Math.min(keyframes.length - 1, rightIndex))];
    const rightCandidate = keyframes[Math.max(0, Math.min(keyframes.length - 1, leftIndex))];

    // Choose the keyframe with the smallest time difference from target
    const leftDistance = Math.abs((leftCandidate?.sec ?? Infinity) - targetTime);
    const rightDistance = Math.abs((rightCandidate?.sec ?? Infinity) - targetTime);
    const closestKeyframe = leftDistance <= rightDistance ? leftCandidate : rightCandidate;

    return closestKeyframe?.url ?? null;
}

async function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
    let t: any;
    const timer = new Promise<never>(
        (_, rej) => (t = setTimeout(() => rej(new Error(`${label} timed out after ${ms}ms`)), ms))
    );
    try {
        return await Promise.race([p, timer]);
    } finally {
        clearTimeout(t);
    }
}

// ------------------------------------------------------

/**
 * Generates video thumbnails using FFmpeg's accurate single-decode approach.
 *
 * This function optimizes thumbnail generation by:
 * 1. Creating a decode window that spans only the requested time range
 * 2. Using FFmpeg's accurate seeking to minimize unnecessary decoding
 * 3. Selecting specific frames within the window to extract thumbnails
 *
 * @param ffmpeg - FFmpeg.wasm instance for video processing
 * @param requests - Array of thumbnail requests with time (seconds) and index
 * @param outDir - Output directory for temporary thumbnail files
 * @param inputFileName - Path to the input video file
 * @param quality - JPEG quality (1-31, lower is better quality)
 * @param scale - FFmpeg scale filter string (e.g., "scale=320:240")
 * @param fps - Video frame rate for time-to-frame calculations (default: 30)
 * @returns Promise resolving to array of thumbnails with time, index, and base64 data
 * @throws Error if FFmpeg execution fails or produces no output files
 */
async function generateVideoThumbnailsViaFFMPEGAccurateSingleDecode(
    ffmpeg: any,
    requests: { time: number; index: number }[],
    outDir: string,
    inputFileName: string,
    quality: number,
    scale: string,
    fps: number = 30
) {
    // Create a decode window that spans only the requested time range to minimize work
    const times = requests.map((r) => Math.max(0.0005, r.time)); // Ensure minimum time to avoid edge cases
    const minT = Math.min(...times);
    const maxT = Math.max(...times);

    // Add safety margins around the time window to account for seeking precision
    const preRoll = 0.15; // Start slightly before the earliest requested time
    const postPad = 0.25; // End slightly after the latest requested time
    const startFast = Math.max(0, minT - preRoll);
    const durWin = Math.max(0.1, maxT - minT + preRoll + postPad);

    // Convert requested times to frame indices relative to the decode window start
    const baseFrame = Math.max(0, Math.floor(startFast * fps));
    const req = requests.map(({ time, index }) => {
        const t = Math.max(0.0005, time); // Ensure minimum time
        const abs = Math.max(0, Math.round(t * fps)); // Absolute frame number
        const rel = Math.max(0, abs - baseFrame); // Frame relative to window start
        return { time: t, index, frameRel: rel };
    });

    // Get unique frame indices to avoid duplicate processing
    const uniqueFrames = [...new Set(req.map((r) => r.frameRel))].sort((a, b) => a - b);
    if (!uniqueFrames.length) {
        return requests.map(({ time, index }) => ({ time, index, thumbnail: null }));
    }

    // Build FFmpeg select filter expression to extract only the needed frames
    // Format: select='eq(n,frame1)+eq(n,frame2)+...' where n is frame number
    const selectExpr = `select='${uniqueFrames.map((n) => `eq(n\\,${n})`).join("+")}'`;
    const outPattern = `${outDir}/acc_%03d.jpg`;

    // Construct FFmpeg command for single-pass accurate thumbnail extraction
    const argsAccurate = [
        "-hide_banner", // Suppress FFmpeg banner
        "-nostdin", // Disable stdin to prevent hanging
        "-loglevel",
        "error", // Only show errors
        "-probesize",
        "32k", // Limit initial probe size for faster startup
        "-analyzeduration",
        "0", // Skip stream analysis for speed
        "-ss",
        startFast.toFixed(3), // Fast seek to window start (before -i for efficiency)
        "-i",
        inputFileName, // Input video file
        "-t",
        durWin.toFixed(3), // Limit decode duration to window size
        "-map",
        "0:v:0", // Explicitly map first video stream
        "-an", // Disable audio
        "-sn", // Disable subtitles
        "-dn", // Disable data streams
        "-vf",
        `${selectExpr},${scale}`, // Apply frame selection and scaling filters
        "-fps_mode",
        "vfr", // Variable frame rate mode
        "-pix_fmt",
        "yuvj420p", // JPEG-compatible pixel format
        "-q:v",
        String(quality), // JPEG quality setting
        "-f",
        "image2", // Image sequence format
        "-y", // Overwrite output files
        outPattern // Output filename pattern
    ];

    // Calculate adaptive timeout based on decode window duration
    // Use ~3.5x multiplier for WASM overhead, with reasonable bounds
    const execTimeoutMs = Math.max(6000, Math.min(45000, Math.ceil(durWin * 3500)));

    // Execute FFmpeg command with timeout protection
    await withTimeout(ffmpeg.exec(argsAccurate), execTimeoutMs, "single-pass accurate");

    // Retrieve generated thumbnail files from FFmpeg filesystem
    const namesAcc = (await listFiles(ffmpeg, outDir))
        .filter((n) => /^acc_\d{3}\.jpg$/.test(n)) // Match expected filename pattern
        .sort(); // Ensure consistent ordering

    if (!namesAcc.length) {
        throw new Error("single-pass accurate produced no files");
    }

    // Convert thumbnail files to base64 data URLs and map to frame indices
    const frameToUrl = new Map<number, string>();
    for (let i = 0; i < namesAcc.length; i++) {
        const name = namesAcc[i];

        // Read thumbnail file data with timeout protection
        const data = await withTimeout(
            ffmpeg.readFile(`${outDir}/${name}`),
            8000,
            `readFile ${name}`
        );

        // Convert binary data to base64 data URL
        const base64 = btoa(String.fromCharCode(...new Uint8Array(data as ArrayBuffer)));
        frameToUrl.set(uniqueFrames[i], `data:image/jpeg;base64,${base64}`);

        // Clean up temporary file (ignore errors)
        try {
            await ffmpeg.deleteFile(`${outDir}/${name}`);
        } catch {}
    }

    // Map thumbnails back to original requests using relative frame indices
    return req.map(({ time, index, frameRel }) => ({
        time,
        index,
        thumbnail: frameToUrl.get(frameRel) ?? null
    }));
}

/**
 * NOTE: this method is not used anymore, but is kept here in case we want to use it again in the future.
 * NOTE: this method is memory intensive and may have opportunities for optimization.
 *
 * Generates video thumbnails using precise seeking for each timestamp.
 * This method performs individual FFmpeg operations for each unique timestamp,
 * making it ideal for short clips (≤10s) with moderate thumbnail counts (~8).
 *
 * Strategy: Uses accurate seeking (-ss after -i) which is slower but guarantees
 * frame-perfect accuracy by seeking to the exact timestamp after input parsing.
 *
 * @param ffmpeg - The FFmpeg instance to use for processing
 * @param requests - Array of thumbnail requests with time and index
 * @param outDir - Output directory path in FFmpeg's virtual filesystem
 * @param inputFileName - Input video file name in FFmpeg's virtual filesystem
 * @param quality - JPEG quality (1-31, lower is better quality)
 * @param scale - FFmpeg scale filter string (e.g., "scale=320:240")
 * @param onThumbnailGenerated - Optional callback fired when each thumbnail is ready
 * @returns Promise resolving to array of thumbnail results with base64 data URLs
 */
async function generateVideoThumbnailsViaFFMPEGAccuratePreciseSeeks(
    ffmpeg: any,
    requests: { time: number; index: number }[],
    outDir: string,
    inputFileName: string,
    quality: number,
    scale: string,
    onThumbnailGenerated?: ({
        time,
        index,
        thumbnail
    }: {
        time: number;
        index: number;
        thumbnail: string;
    }) => void
) {
    // Extract unique timestamps and sort them for consistent processing
    // Deduplication prevents redundant work when multiple requests have same timestamp
    const uniqueTimes = [...new Set(requests.map((r) => r.time))].sort((a, b) => a - b);
    const timeToUrl = new Map<number, string>();

    // Process each unique timestamp with precise seeking
    for (const [index, originalTime] of uniqueTimes.entries()) {
        // Nudge away from exact 0 to avoid decoder edge cases at video start
        const seekTime = Math.max(0.0005, originalTime);

        // Generate unique output filename based on timestamp
        const outputFileName = `${outDir}/one_${Math.round(seekTime * 1000)
            .toString()
            .padStart(6, "0")}.jpg`;

        // Build FFmpeg command for precise frame extraction
        const ffmpegArgs = [
            "-hide_banner", // Suppress banner output
            "-nostdin", // Disable stdin to prevent hanging
            "-loglevel",
            "error", // Only show errors
            "-probesize",
            "32k", // Minimal probing for faster startup
            "-analyzeduration",
            "0", // Skip analysis for speed
            "-i",
            inputFileName, // Input video file
            "-ss",
            seekTime.toFixed(6), // Seek to exact timestamp (AFTER -i for accuracy)
            "-map",
            "0:v:0", // Explicitly map first video stream
            "-an",
            "-sn",
            "-dn", // Disable audio, subtitle, and data streams
            "-frames:v",
            "1", // Extract exactly one frame
            "-vf",
            scale, // Apply scaling filter
            "-pix_fmt",
            "yuvj420p", // Use JPEG-compatible pixel format
            "-f",
            "image2", // Output as image
            "-q:v",
            String(quality), // Set JPEG quality
            "-y", // Overwrite output file
            outputFileName
        ];

        try {
            // Execute FFmpeg command for frame extraction
            await ffmpeg.exec(ffmpegArgs);
            // Read the generated thumbnail and convert to base64 data URL
            const thumbnailBuffer = await ffmpeg.readFile(outputFileName);
            const base64Data = btoa(
                String.fromCharCode(...new Uint8Array(thumbnailBuffer as ArrayBuffer))
            );
            const dataUrl = `data:image/jpeg;base64,${base64Data}`;

            // Store the result and notify callback if provided
            timeToUrl.set(seekTime, dataUrl);
            onThumbnailGenerated?.({ time: seekTime, index, thumbnail: dataUrl });
        } catch (e) {
            console.warn("Failed to create thumbnail file for time", seekTime, "index", index, e);
        }

        // Clean up temporary file to prevent filesystem bloat
        try {
            await ffmpeg.deleteFile(outputFileName);
        } catch (e) {
            console.warn("Failed to delete thumbnail file for time", seekTime, "index", index, e);
        }
    }

    // Map results back to original request format, handling duplicates
    return requests.map(({ time, index }) => ({
        time,
        index,
        thumbnail: timeToUrl.get(time) ?? null
    }));
}

/**
 * Generates video thumbnails using fast keyframe extraction.
 * NOTE: this method is memory intensive and may have opportunities for optimization.
 *
 * This method extracts ALL keyframes in a single FFmpeg pass, then maps requested
 * timestamps to the nearest available keyframe. Much faster than precise seeking
 * but less accurate since it's limited to existing keyframe positions.
 *
 * Strategy: Extract all I-frames (keyframes) at once, then use binary search
 * to find the closest keyframe for each requested timestamp.
 *
 * @param ffmpeg - The FFmpeg instance to use for processing
 * @param requests - Array of thumbnail requests with time and index
 * @param outDir - Output directory path in FFmpeg's virtual filesystem
 * @param inputFileName - Input video file name in FFmpeg's virtual filesystem
 * @param quality - JPEG quality (1-31, lower is better quality)
 * @param scale - FFmpeg scale filter string (e.g., "scale=320:240")
 * @returns Promise resolving to array of thumbnail results with base64 data URLs
 */
async function generateVideoThumbnailsViaFFMPEGFast(
    ffmpeg: any,
    requests: { time: number; index: number }[],
    outDir: string,
    inputFileName: string,
    quality: number,
    scale: string
) {
    // STEP 1: Extract ALL keyframes in a single FFmpeg pass
    // Filenames will contain raw PTS (Presentation Time Stamp) values
    const outPattern = `${outDir}/kf_%010d.jpg`; // %d gets replaced by PTS due to -frame_pts 1

    // Build FFmpeg arguments with optional keyframe-only decoding optimization
    const makeArgs = (skipNonKeyFrames: boolean) => [
        "-hide_banner",
        "-loglevel",
        "info", // Need info level to extract time base from logs
        "-probesize",
        "32k", // Minimal probing for faster startup
        "-analyzeduration",
        "0", // Skip analysis for speed
        ...(skipNonKeyFrames ? ["-skip_frame", "nokey"] : []), // OPTIMIZATION: skip non-keyframes during decode
        "-i",
        inputFileName,
        "-an",
        "-sn",
        "-dn", // Disable audio, subtitle, and data streams
        "-vf",
        `select=eq(pict_type\\,I),${scale}`, // Filter: keep only I-frames (keyframes), then scale
        "-fps_mode",
        "vfr", // Variable frame rate (replaces deprecated -vsync 0)
        "-frame_pts",
        "1", // Use PTS values as filenames for timestamp tracking
        "-pix_fmt",
        "yuvj420p", // JPEG-compatible pixel format
        "-q:v",
        String(quality), // Set JPEG quality
        "-f",
        "image2", // Output as image sequence
        "-y",
        outPattern // Overwrite output files
    ];

    // Try fast extraction first (skip non-keyframes during decode)
    let { text: execLog } = await captureLogs(ffmpeg, () => ffmpeg.exec(makeArgs(true)));
    let keyframeFiles = (await listFiles(ffmpeg, outDir))
        .filter((filename) => /^kf_\d{10}\.jpg$/.test(filename))
        .sort();

    // Fallback: if no keyframes extracted, try without skip optimization
    if (!keyframeFiles.length) {
        const fallbackResult = await captureLogs(ffmpeg, () => ffmpeg.exec(makeArgs(false)));
        execLog += `\n${fallbackResult.text}`;
        keyframeFiles = (await listFiles(ffmpeg, outDir))
            .filter((filename) => /^kf_\d{10}\.jpg$/.test(filename))
            .sort();
    }

    // If still no keyframes, return null thumbnails
    if (!keyframeFiles.length) {
        return requests.map(({ time, index }) => ({ time, index, thumbnail: null }));
    }

    // STEP 2: Extract time base (tbn) to convert PTS timestamps to seconds
    // Formula: seconds = PTS / timeBase
    let timeBase = parseTbnFrom(execLog);

    // Fallback: probe the video file if time base not found in logs
    if (!timeBase) {
        const probeResult = await captureLogs(
            ffmpeg,
            () =>
                ffmpeg
                    .exec(["-hide_banner", "-loglevel", "info", "-i", inputFileName])
                    .catch(() => Promise.resolve(undefined as any)) // Ignore probe errors
        );
        timeBase = parseTbnFrom(probeResult.text);
    }

    // Last resort: estimate time base from frame rate
    if (!timeBase) {
        const frameRate = parseFpsFrom(execLog) ?? 30; // Default to 30 FPS if unknown
        timeBase = Math.round(frameRate);
    }

    // STEP 3: Build keyframe index with timestamps and data URLs
    const keyframes: { sec: number; url: string }[] = [];
    for (const filename of keyframeFiles) {
        const fullPath = `${outDir}/${filename}`;
        try {
            // Extract PTS from filename: "kf_1234567890.jpg" -> 1234567890
            const ptsValue = Number(filename.slice(3, 13));
            const timestampSeconds = ptsValue / timeBase;

            // Read image data and convert to base64 data URL
            const imageBuffer = await ffmpeg.readFile(fullPath);
            const base64Data = btoa(
                String.fromCharCode(...new Uint8Array(imageBuffer as ArrayBuffer))
            );
            const dataUrl = `data:image/jpeg;base64,${base64Data}`;

            keyframes.push({ sec: timestampSeconds, url: dataUrl });

            // Clean up temporary file
            await ffmpeg.deleteFile(fullPath);
        } catch {
            // Skip failed keyframes silently
        }
    }

    // Sort keyframes by timestamp for binary search efficiency
    keyframes.sort((a, b) => a.sec - b.sec);

    // STEP 4: Map each requested timestamp to the nearest available keyframe
    return requests.map(({ time, index }) => ({
        time,
        index,
        thumbnail: nearestUrl(keyframes, time) // Binary search for closest keyframe
    }));
}

async function generateVideoThumbnailsViaFFMPEG(
    context: PreparedVideoContext,
    times: number[],
    size: number = 64,
    quality: number = 6,
    mode: ThumbnailMode = ThumbnailMode.FAST, // NEW: choose "fast" (keyframes) or "accurate" (full decode)
    _onThumbnailGenerated?: ({
        time,
        index,
        thumbnail
    }: {
        time: number;
        index: number;
        thumbnail: string;
    }) => void
): Promise<ThumbnailResult[]> {
    const { ffmpeg, inputFileName } = context;

    const requests = times.map((t, index) => ({ time: Math.max(0, t), index }));
    const outDir = "thumbs";
    await ensureDir(ffmpeg, outDir);

    const scale = `scale='min(${size},iw)':'-2':flags=fast_bilinear`; // no upscale, even height

    // ---------- MODE: FAST (keyframes only) ----------
    if (mode === ThumbnailMode.FAST) {
        return generateVideoThumbnailsViaFFMPEGFast(
            ffmpeg,
            requests,
            outDir,
            inputFileName,
            quality,
            scale
        );
    } else if (mode === ThumbnailMode.ACCURATE) {
        const thumbnails = await generateVideoThumbnailsViaFFMPEGAccurateSingleDecode(
            ffmpeg,
            requests,
            outDir,
            inputFileName,
            quality,
            scale
        );
        return thumbnails;
        // return generateVideoThumbnailsViaFFMPEGAccuratePreciseSeeks(
        //     ffmpeg,
        //     requests,
        //     outDir,
        //     inputFileName,
        //     quality,
        //     scale,
        //     onThumbnailGenerated
        // );
    } else {
        throw new Error(`Invalid thumbnail mode: ${mode}`);
    }
}

const generateVideoThumbnailsViaCanvas = async (
    file: File,
    thumbnailRequests: ThumbnailRequest[]
): Promise<ThumbnailResult[]> => {
    const fallbackResults: ThumbnailResult[] = [];

    for (let i = 0; i < thumbnailRequests.length; i++) {
        const request = thumbnailRequests[i];
        try {
            const thumbnail = await generateVideoThumbnailViaCanvas(file, {
                currentTime: request.currentTime
            });

            fallbackResults.push({ time: request.currentTime, thumbnail, index: i });
        } catch (fallbackError) {
            console.warn(
                `🎬 [generateVideoThumbnailsViaCanvas] Fallback failed for thumbnail at ${request.currentTime}s:`,
                fallbackError
            );
            fallbackResults.push({ time: request.currentTime, thumbnail: null, index: i });
        }
    }

    return fallbackResults;
};

/**
 * Generate multiple thumbnails from a single video file efficiently
 * Loads the video once and processes thumbnails in parallel batches
 */

class SafariBrowserRequiresFFMPEGError extends Error {
    name = "SafariBrowserRequiresFFMPEGError";
}

export async function generateVideoThumbnails(
    file: File,
    thumbnailRequests: ThumbnailRequest[],
    options: { size?: number; quality?: number; mode?: ThumbnailMode; isSafari?: boolean } = {
        size: 128,
        quality: 6,
        mode: ThumbnailMode.FAST
    }
): Promise<ThumbnailResult[]> {
    if (!file.type.startsWith("video/")) {
        throw new Error("generateVideoThumbnails only supports video files");
    }

    if (thumbnailRequests.length === 0) {
        return [];
    }

    // Add index to requests for result tracking
    const indexedRequests = thumbnailRequests.map((request, index) => ({ ...request, index }));

    const isSafari = isSafariBrowser();

    try {
        if (isSafari) {
            throw new SafariBrowserRequiresFFMPEGError("Safari requires ffmpeg");
        }

        // prioritize canvas for everything except safari
        const results = await generateVideoThumbnailsViaCanvas(file, indexedRequests);

        // Sort results by original index to maintain order
        return results.sort((a, b) => a.index - b.index);
    } catch (error) {
        if (error instanceof SafariBrowserRequiresFFMPEGError) {
            // do nothing
        } else {
            console.error(
                `🎬 [generateVideoThumbnails] Failed to generate thumbnails via canvas:`,
                error,
                "falling back to ffmpeg"
            );
        }

        let context: PreparedVideoContext | null = null;

        try {
            // Prepare video (load into FFmpeg filesystem once)
            context = await prepareVideoForThumbnails(file);

            const results: ThumbnailResult[] = await generateVideoThumbnailsViaFFMPEG(
                context!,
                indexedRequests.map((r) => r.currentTime),
                options.size,
                options.quality,
                options.mode
            );

            // Sort results by original index to maintain order
            return results.sort((a, b) => a.index - b.index);
        } catch (error) {
            console.error(
                `🎬 [generateVideoThumbnails] Failed to generate thumbnails via ffmpeg:`,
                error
            );
            return [];
        } finally {
            // Always cleanup ffmpeg video file
            if (context) {
                await context.cleanup();
            }
        }
    }
}

/**
 * Fallback canvas-based thumbnail generation for videos
 */
async function generateVideoThumbnailViaCanvas(
    file: File,
    options: { currentTime?: number } = {}
): Promise<string | null> {
    const videoManager = createSafeVideoElement(file, {
        muted: true,
        playsInline: true,
        crossOrigin: "anonymous"
    });
    const video = videoManager.element;

    try {
        // Wait for metadata to load
        await new Promise<void>((resolve, reject) => {
            video.onloadedmetadata = () => resolve();
            video.onerror = () => reject(new Error("Failed to load video metadata"));
            video.load();
        });

        video.currentTime = options.currentTime || 0;

        // Wait for seek to complete
        await new Promise<void>((resolve, reject) => {
            video.onseeked = () => resolve();
            video.onerror = () => reject(new Error("Failed to seek video"));
        });

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
            console.warn("Failed to get 2D context for canvas, returning null");
            videoManager.cleanup();
            return null;
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const result = canvas.toDataURL("image/jpeg", 0.8);
        videoManager.cleanup();
        return result;
    } catch (error) {
        console.warn("Failed to generate video thumbnail with canvas:", error);
        videoManager.cleanup();
        return null;
    }
}

/**
 * Generate a thumbnail for an image file
 */
async function generateImageThumbnailViaCanvas(
    file: File,
    options: { size?: number } = {}
): Promise<string | null> {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    try {
        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error("Failed to load image"));
            img.src = objectUrl;
        });

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
            URL.revokeObjectURL(objectUrl);
            return null;
        }

        const thumbnailSize = options.size || 64;
        canvas.width = thumbnailSize;
        canvas.height = thumbnailSize;

        const aspectRatio = img.width / img.height;
        let drawWidth, drawHeight, drawX, drawY;

        if (aspectRatio > 1) {
            drawWidth = thumbnailSize;
            drawHeight = thumbnailSize / aspectRatio;
            drawX = 0;
            drawY = (thumbnailSize - drawHeight) / 2;
        } else {
            drawWidth = thumbnailSize * aspectRatio;
            drawHeight = thumbnailSize;
            drawX = (thumbnailSize - drawWidth) / 2;
            drawY = 0;
        }

        ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
        URL.revokeObjectURL(objectUrl);
        return canvas.toDataURL("image/jpeg", 0.8);
    } catch (error) {
        console.warn("Failed to generate image thumbnail:", error);
        URL.revokeObjectURL(objectUrl);
        return null;
    }
}

/**
 * Generate a thumbnail for any file (video or image)
 */
export const generateFileThumbnail = async (
    file: File,
    options: { currentTime?: number; size?: number } = {}
): Promise<string | null> => {
    const isVideo = file.type.startsWith("video/");

    try {
        if (isVideo) {
            const results = await generateVideoThumbnails(
                file,
                [{ currentTime: options.currentTime || 0 }],
                { size: options.size }
            );

            if (results.length > 0 && results[0].thumbnail) return results[0].thumbnail;

            console.warn("🎬 [generateFileThumbnail] Failed to generate thumbnail, no results");
            return null;
        } else {
            return generateImageThumbnailViaCanvas(file, options);
        }
    } catch (error) {
        console.warn("Failed to generate file thumbnail:", error);
        return null;
    }
};
